import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  searchPages,
  getAllPages,
  getPageById,
  createPage,
  updatePage,
  deletePage,
  togglePageActive,
} from "../../../services/pagesServices";

import {
  FiFileText,
  FiPlus,
  FiRefreshCcw,
  FiSearch,
} from "react-icons/fi";

import FiltersBar from "./components/FiltersBar";
import PageTable from "./components/PageTable";
import PageForm from "./components/PageForm";
import PagePreviewDrawer from "./components/PagePreviewDrawer";
import ConfirmDeleteDrawer from "./components/ConfirmDeleteDrawer";

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString("fr-FR", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "—";

export default function PagesAdmin() {
  const navigate = useNavigate();

  const [q, setQ] = useState("");
  const [locale, setLocale] = useState("fr");
  const [parentId, setParentId] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sort, setSort] = useState("created_at");
  const [dir, setDir] = useState("DESC");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [allPages, setAllPages] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [initial, setInitial] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPage, setPreviewPage] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [message, setMessage] = useState("");

  const totalPages = useMemo(
    () => Math.max(Math.ceil(total / limit), 1),
    [total, limit]
  );

  const parentsMap = useMemo(() => {
    const map = new Map();

    (allPages || []).forEach((p) => {
      map.set(p.id, p);
    });

    return map;
  }, [allPages]);

  const stats = useMemo(() => {
    const pages = Array.isArray(allPages) ? allPages : [];

    return {
      total: pages.length,
      active: pages.filter((p) => !!p.is_active).length,
      inactive: pages.filter((p) => !p.is_active).length,
      root: pages.filter((p) => !p.parent_id).length,
    };
  }, [allPages]);

  const fetchAllPages = async () => {
    try {
      const data = await getAllPages();
      setAllPages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur fetchAllPages:", error);
    }
  };

  const fetchList = async (options = {}) => {
    const pageToLoad = options.pageOverride || page;

    try {
      setLoading(true);

      const res = await searchPages({
        q: q.trim() || undefined,
        locale,
        parent_id: parentId ? Number(parentId) : undefined,
        is_active:
          activeFilter === "all"
            ? undefined
            : activeFilter === "active"
            ? true
            : false,
        page: pageToLoad,
        limit,
        sort,
        dir,
      });

      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (error) {
      console.error("Erreur fetchList:", error);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPages();
  }, []);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, locale, parentId, activeFilter, sort, dir]);

  const refresh = async () => {
    setMessage("");
    await Promise.all([fetchAllPages(), fetchList()]);
  };

  const resetFilters = async () => {
    setQ("");
    setLocale("fr");
    setParentId("");
    setActiveFilter("all");
    setLimit(20);
    setSort("created_at");
    setDir("DESC");
    setPage(1);

    setTimeout(() => {
      fetchList({ pageOverride: 1 });
    }, 0);
  };

  const onSubmitSearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    fetchList({ pageOverride: 1 });
  };

  const openCreate = () => {
    setMessage("");
    setInitial(null);
    setFormOpen(true);
  };

  const openEdit = async (id) => {
    try {
      setMessage("");

      const row = await getPageById(id);

      if (!row) {
        setMessage("Page introuvable.");
        return;
      }

      setInitial(row);
      setFormOpen(true);
    } catch (error) {
      console.error("Erreur openEdit:", error);
      setMessage("Impossible d’ouvrir la page.");
    }
  };

  const openPreview = async (id) => {
    try {
      setPreviewLoading(true);
      setPreviewOpen(true);
      setPreviewPage(null);

      const row = await getPageById(id);

      if (!row) {
        setMessage("Page introuvable.");
        setPreviewOpen(false);
        return;
      }

      setPreviewPage(row);
    } catch (error) {
      console.error("Erreur openPreview:", error);
      setMessage("Impossible de charger l’aperçu.");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const onSave = async (payload, id = null, afterSave = "close") => {
    try {
      setMessage("");

      const saved = id
        ? await updatePage(id, payload)
        : await createPage(payload);

      if (!saved?.id) {
        throw new Error("Sauvegarde impossible.");
      }

      setFormOpen(false);
      setInitial(null);

      await Promise.all([fetchAllPages(), fetchList()]);

      setMessage(id ? "Page modifiée avec succès." : "Page créée avec succès.");

      if (afterSave === "builder") {
        navigate(`/admin/pages/${saved.id}/builder`);
      }

      return saved;
    } catch (error) {
      console.error("Erreur onSave:", error);
      setMessage(
        "Erreur lors de l’enregistrement. Vérifiez le slug et les champs obligatoires."
      );
      throw error;
    }
  };

  const onToggle = async (id) => {
    try {
      setMessage("");

      const updated = await togglePageActive(id);

      if (!updated) {
        throw new Error("Mise à jour impossible.");
      }

      await Promise.all([fetchAllPages(), fetchList()]);
      setMessage(updated.is_active ? "Page activée." : "Page désactivée.");
    } catch (error) {
      console.error("Erreur onToggle:", error);
      setMessage("Impossible de modifier le statut de la page.");
    }
  };

  const askDelete = (row) => {
    setPageToDelete(row);
    setConfirmOpen(true);
  };

  const onDelete = async () => {
    if (!pageToDelete?.id) return;

    try {
      setDeleting(true);
      setMessage("");

      const ok = await deletePage(pageToDelete.id);

      if (!ok) {
        throw new Error("Suppression impossible.");
      }

      setConfirmOpen(false);
      setPageToDelete(null);

      await Promise.all([fetchAllPages(), fetchList()]);

      setMessage("Page supprimée avec succès.");
    } catch (error) {
      console.error("Erreur onDelete:", error);
      setMessage(
        "Impossible de supprimer cette page. Vérifiez qu’elle ne possède pas de sous-pages."
      );
    } finally {
      setDeleting(false);
    }
  };

  const toggleSort = (key) => {
    if (sort === key) {
      setDir((current) => (current === "ASC" ? "DESC" : "ASC"));
      return;
    }

    setSort(key);
    setDir("ASC");
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 text-slate-950 dark:bg-slate-950 dark:text-slate-50 md:p-6">
      <div className="mx-auto max-w-[1500px]">
        <header className="overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="relative p-5 md:p-7">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-sky-600 via-cyan-400 to-emerald-400" />

            <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 text-xs font-bold text-sky-700 dark:bg-sky-950/40 dark:text-sky-200">
                  <FiFileText />
                  CMS MonAppliMairie
                </div>

                <h1 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">
                  Pages personnalisées
                </h1>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                  Créez les pages, organisez l’arborescence, gérez le
                  référencement et ouvrez le builder simplifié pour composer le
                  contenu.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={openCreate}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-sky-700"
                >
                  <FiPlus className="text-lg" />
                  Nouvelle page
                </button>

                <button
                  type="button"
                  onClick={refresh}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FiRefreshCcw />
                  Rafraîchir
                </button>
              </div>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <StatCard label="Pages au total" value={stats.total} />
              <StatCard label="Pages actives" value={stats.active} tone="green" />
              <StatCard label="Pages inactives" value={stats.inactive} tone="rose" />
              <StatCard label="Pages racines" value={stats.root} tone="blue" />
            </div>
          </div>
        </header>

        {message ? (
          <div
            className={`mt-5 rounded-3xl border px-4 py-3 text-sm font-medium ${
              message.includes("Erreur") ||
              message.includes("Impossible") ||
              message.includes("introuvable")
                ? "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200"
                : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200"
            }`}
          >
            {message}
          </div>
        ) : null}

        <section className="mt-6 rounded-[32px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 md:p-5">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-950 text-white dark:bg-slate-100 dark:text-slate-950">
              <FiSearch />
            </div>

            <div>
              <h2 className="text-base font-bold">Recherche et filtres</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Filtrez les pages par titre, parent, langue ou statut.
              </p>
            </div>
          </div>

          <FiltersBar
            q={q}
            onQ={setQ}
            locale={locale}
            onLocale={(value) => {
              setLocale(value);
              setPage(1);
            }}
            parentId={parentId}
            onParentId={(value) => {
              setParentId(value);
              setPage(1);
            }}
            parents={allPages}
            activeFilter={activeFilter}
            onActiveFilter={(value) => {
              setActiveFilter(value);
              setPage(1);
            }}
            limit={limit}
            onLimit={(value) => {
              setLimit(value);
              setPage(1);
            }}
            onSubmit={onSubmitSearch}
            onReset={resetFilters}
          />
        </section>

        <section className="mt-6">
          <PageTable
            loading={loading}
            items={items}
            parentsMap={parentsMap}
            page={page}
            total={total}
            totalPages={totalPages}
            onPrev={() => setPage((current) => Math.max(current - 1, 1))}
            onNext={() =>
              setPage((current) => Math.min(current + 1, totalPages))
            }
            onEdit={openEdit}
            onToggle={onToggle}
            onDelete={askDelete}
            onPreview={openPreview}
            onContent={(id) => navigate(`/admin/pages/${id}/builder`)}
            sort={sort}
            dir={dir}
            onSort={toggleSort}
            fmtDate={fmtDate}
          />
        </section>
      </div>

      <PageForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setInitial(null);
        }}
        onSave={onSave}
        initial={initial}
        parents={allPages}
        onOpenBuilder={(pageId) => navigate(`/admin/pages/${pageId}/builder`)}
      />

      <PagePreviewDrawer
        open={previewOpen}
        loading={previewLoading}
        page={previewPage}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewPage(null);
        }}
      />

      <ConfirmDeleteDrawer
        open={confirmOpen}
        page={pageToDelete}
        loading={deleting}
        onClose={() => {
          if (deleting) return;
          setConfirmOpen(false);
          setPageToDelete(null);
        }}
        onConfirm={onDelete}
      />
    </div>
  );
}

function StatCard({ label, value, tone = "slate" }) {
  const toneClasses = {
    slate:
      "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-950 dark:text-slate-200 dark:border-slate-800",
    green:
      "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-200 dark:border-emerald-900/50",
    rose:
      "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-200 dark:border-rose-900/50",
    blue:
      "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/30 dark:text-sky-200 dark:border-sky-900/50",
  };

  return (
    <div
      className={`rounded-3xl border p-4 shadow-sm ${
        toneClasses[tone] || toneClasses.slate
      }`}
    >
      <div className="text-xs font-bold uppercase tracking-wide opacity-75">
        {label}
      </div>
      <div className="mt-2 text-3xl font-bold">{value}</div>
    </div>
  );
}